import { useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowUpRight, Clock } from "lucide-react";
import { SERVICE_PAGES } from "../pages";
import { SERVICES, CONTACT } from "../data";
import { Reveal, SectionLabel } from "./Reveal";
import { CompareSlider } from "./CompareSlider";
import { useQuote } from "../context/QuoteContext";
import { useSeo } from "../hooks/useSeo";
import PageHero from "./PageHero";
import Faq from "./Faq";

const PRESET_BY_SLUG = {
  gevelreiniging: "Gevel",
  dakreiniging: "Dak",
  dakcoating: "Dak",
  "dak-en-gevelimpregnering": "Gevel en Dak",
  "bedrijfspand-reinigen": "Bedrijfspand",
  "terras-reinigen": "Terras",
};

export const ServicePage = () => {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, "");
  const data = SERVICE_PAGES[slug];
  const preset = PRESET_BY_SLUG[slug] || null;
  const { openQuote } = useQuote();

  useSeo({ title: data?.metaTitle, description: data?.metaDesc });

  if (!data) {
    return (
      <div className="min-h-[60vh] grid place-items-center pt-32">
        <div className="text-center">
          <h1 className="font-display text-3xl font-bold text-ink">Pagina niet gevonden</h1>
          <Link to="/" className="mt-4 inline-block text-teal font-semibold">
            Terug naar home
          </Link>
        </div>
      </div>
    );
  }

  const others = SERVICES.filter((s) => s.path !== `/${slug}`);

  return (
    <div data-testid={`service-page-${slug}`}>
      <PageHero
        title={data.heroTitle}
        subtitle={data.heroSubtitle}
        image={data.heroImage}
        crumb={data.label}
        presetService={preset}
      />

      {/* Lead + sticky quote card */}
      <section className="bg-background py-24 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <SectionLabel>{data.label}</SectionLabel>
            <Reveal>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-ink leading-tight">
                {data.lead.title}
              </h2>
            </Reveal>
            {data.lead.paragraphs.map((p, i) => (
              <Reveal key={i} delay={0.05 * (i + 1)}>
                <p className="mt-5 text-muted-foreground leading-relaxed">{p}</p>
              </Reveal>
            ))}
          </div>

          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28 rounded-3xl border border-border bg-navy-deep grain overflow-hidden p-8">
              <div className="relative z-10">
                <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-medium text-white/80">
                  <Clock className="h-4 w-4 text-sky" /> In 2 minuten klaar
                </div>
                <h3 className="mt-5 font-display text-2xl font-bold text-white">
                  Offerte aanvragen
                </h3>
                <p className="mt-3 text-sm text-white/60 leading-relaxed">
                  Nog dezelfde dag een persoonlijke, vrijblijvende offerte op
                  maat — inclusief gratis proefstuk.
                </p>
                <button
                  onClick={() => openQuote(preset)}
                  data-testid="service-quote-cta"
                  className="group mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-teal px-6 py-4 font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5"
                >
                  Gratis offerte aanvragen
                  <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
                <a
                  href={CONTACT.phoneHref}
                  className="mt-3 block text-center text-sm text-white/50 hover:text-white"
                >
                  of bel {CONTACT.phone}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-secondary py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-ink max-w-2xl leading-tight">
              {data.benefitsTitle}
            </h2>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.benefits.map((b, i) => (
              <Reveal key={b} delay={i * 0.05}>
                <div className="flex items-center gap-3 rounded-2xl border border-border bg-card px-5 py-4">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-teal/10 text-teal">
                    <Check className="h-4 w-4" strokeWidth={3} />
                  </span>
                  <span className="font-medium text-ink text-sm">{b}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Process + compare slider */}
      <section className="bg-background py-24 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div>
              <SectionLabel>Werkwijze</SectionLabel>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-ink leading-tight">
                {data.process.title}
              </h2>
              {data.process.paragraphs.map((p, i) => (
                <p key={i} className="mt-5 text-muted-foreground leading-relaxed">
                  {p}
                </p>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <CompareSlider
              item={{ before: data.before, after: data.after, title: "" }}
              testid="service-slider"
            />
          </Reveal>
        </div>
      </section>

      {/* Content sections */}
      <section className="bg-secondary py-24 sm:py-28">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="space-y-14">
            {data.sections.map((sec, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div className="border-l-2 border-teal pl-6 sm:pl-8">
                  <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-ink">
                    {sec.heading}
                  </h3>
                  {sec.body && (
                    <p className="mt-4 text-muted-foreground leading-relaxed">
                      {sec.body}
                    </p>
                  )}
                  {sec.list && (
                    <ul className="mt-5 space-y-3">
                      {sec.list.map((item, j) => (
                        <li key={j} className="flex items-start gap-3">
                          <Check className="mt-1 h-4 w-4 shrink-0 text-teal" strokeWidth={3} />
                          <span className="text-ink/80 leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Other services */}
      <section className="bg-background py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <SectionLabel>Meer diensten</SectionLabel>
          <div className="mt-6 grid sm:grid-cols-3 gap-6">
            {others.map((s) => (
              <Link
                key={s.id}
                to={s.path}
                data-testid={`other-service-${s.id}`}
                className="group relative overflow-hidden rounded-3xl border border-border bg-navy-deep h-52"
              >
                <img
                  src={s.image}
                  alt={`${s.title} door SoftPro`}
                  className="absolute inset-0 h-full w-full object-cover opacity-60 transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-deep to-transparent" />
                <div className="relative z-10 flex h-full items-end justify-between p-6">
                  <span className="font-display text-xl font-bold text-white">
                    {s.title}
                  </span>
                  <ArrowUpRight className="h-6 w-6 text-teal transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Faq />
    </div>
  );
};

export default ServicePage;
